import { useEffect, useState } from "react";
import { Manifest, fetchManifest } from "./manifest";
import { onBlockLoadError, onManifestLoaded } from "./useBlockReadiness";

type UseManifestProps = Readonly<{
    name: string;
    version: string;
    baseUrl: string;
    enabled?: boolean;
}>;
export function useManifest({ name, version, baseUrl, enabled = true }: UseManifestProps): Manifest | null {
    const [state, setState] = useState<UseManifestState>({ status: "loading" });

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const abortController = new AbortController();
        // Promise returned by fetchManifest can't be cancelled
        // because it's shared between multiple components
        fetchManifest(name, version, baseUrl)
            .then((manifest) => {
                onManifestLoaded({ name, version });
                if (abortController.signal.aborted) return;
                setState({ status: "loaded", manifest });
            })
            .catch((error) => {
                onBlockLoadError({ name, version });
                if (abortController.signal.aborted) return;
                setState({ status: "error", error });
            });
        return () => abortController.abort();
    }, [name, version, baseUrl, enabled]);

    switch (state.status) {
        case "loading":
            return null;
        case "error":
            throw state.error;
        case "loaded":
            return state.manifest;
        default:
            assertNever(state);
    }
}

function assertNever(value: never): never {
    throw new Error(`Unreachable: ${value}`);
}

type UseManifestState =
    | { status: "loading" }
    | { status: "error"; error: unknown }
    | { status: "loaded"; manifest: Manifest };
